import AppLayout from '@chegg-wt/core/components/AppLayout'
import { CheggLayoutProvider } from '@chegg-wt/core/components/CheggLayout/CheggLayoutProvider'
import AppHeadTags from '@chegg-wt/core/components/Head/AppHeadTags'
import RoutesContext from '@chegg-wt/core/components/RoutesContext'
import { Config } from '@chegg-wt/core/config'
import useAppendStylesheet from '@chegg-wt/core/hooks/useAppendStylesheet'
import useAppScroll from '@chegg-wt/core/hooks/useAppScroll'
import useCloseModal from '@chegg-wt/core/hooks/useCloseModal'
import useErrorLogger from '@chegg-wt/core/hooks/useErrorLogger'
import useInitGTM from '@chegg-wt/core/hooks/useInitGTM'
import useInitUser from '@chegg-wt/core/hooks/useInitUser'
import useOnPageLoad from '@chegg-wt/core/hooks/useOnPageLoad'
import { useSetVersion } from '@chegg-wt/core/hooks/useSetVersion'
import useUrlStateChange from '@chegg-wt/core/hooks/useUrlStateChange'
import { AppState } from '@chegg-wt/core/state'
import {
  adConfigVersionSelector,
  lvpR3LayoutSelector,
  stickyDisabledSelector,
  svpVariantSelector,
} from '@chegg-wt/core/state/featureFlags/selectors'
import { SiteTheme } from '@chegg-wt/core/theme/styled'
import { useRouter } from 'next/router'
import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { ThemeProvider } from 'styled-components'
// import { useSafeOptimizelyFeature } from '@chegg/optimizely-sdk'
import { getUserAndToken } from '@chegg-wt/core/api/user'
import useCurrentPage from '@chegg-wt/core/hooks/useCurrentPage'
import { isProduction } from '@chegg-wt/core/utils/env'
import { setEbUser } from '@chegg-wt/core/utils/localStorage'
import { Environment, init, listen } from '@chegg/one-auth-sdk'

import { setParaphraseFlag } from '@chegg-wt/core-wa/state/actions'
import { useAuthenticationHook } from '@chegg-wt/core/hooks/useAuthenticationHook'
import { useDeviceID } from '@chegg-wt/core/hooks/useDeviceID'
// import { withOptimizelyComponent } from '../hocs/withOptimizely'
import { authRoutes } from '../constants/user'
import { setUpdateMessagingFlag } from '../state/app/actions'
import {
  setAdUnlockExpVariation,
  setAuthModalExpFlag,
  setAutociteApiVersion,
  setAutociteV0RioOptFlag,
} from '../state/createCitation/actions'
import { isProSelector } from '../state/user/selectors'
import {
  AdUnlockVariations,
  AppRouteCreators,
  FooterColumn,
  GetFromQuery,
  HeadTags,
  IsExact,
  LegalContents,
  NavItems,
} from '../types/core'
import { hasSideViewForRoute } from '../utils/adframe'
// import { useOptimizelyExperiment } from '../hooks/useOptimizelyExperiment'
import { setCitationValidityVariation } from '../state/bibliography/actions'
import { getProfileCode } from './Auth/OneAuth/utils'
import Error404 from './Layouts/Error404'
import { LvpProvider } from './Layouts/LargeVideoPlayerLayout'
import { ExportTabId } from './Modals/ExportModal/types'

const env = isProduction() ? Environment.prod : Environment.test

const authGateUrlSubDomain = isProduction() ? 'auth-gate' : 'auth-gate.playground'

init({
  env,
  clientId: getProfileCode(Config.tokenSiteCode),
  domainUrl: Config.cookieDomain,
  cookieDomain: Config.cookieDomain,
  authGateUrl: `https://${authGateUrlSubDomain}${Config.cookieDomain}/auth-gate/graphql`,
})

const tokenRefreshSuccessCallback = async (token) => {
  if (token) {
    const user = await getUserAndToken()
    setEbUser(user)
  }
}
listen('tokens', tokenRefreshSuccessCallback)

interface Props {
  head: HeadTags
  userAgent: string
  adConfigVersion?: string
  isCheggFree?: boolean
  routes: AppRouteCreators
  theme: SiteTheme
  navItemsSelector: (state: AppState) => NavItems
  notificationBanner?: React.ComponentType
  exportTabIds?: ExportTabId[]
  footerContents: {
    footerLinkColumns: FooterColumn[]
    legalContents: LegalContents
    ccpa: boolean
  }
  hideLayout?: boolean
  getSourceFromQuery?: GetFromQuery
  getStyleFromQuery?: GetFromQuery
  isSourceExact?: IsExact
  isStyleExact?: IsExact
  usesCheggLayout?: boolean
  useAdframeLayout?: boolean
  lvpR3?: boolean
  isPro?: boolean
  stickyDisabled?: boolean
  isAdsFree?: boolean
  getActualStyleId?: (style: string) => string
  enableSvp?: boolean
  svpVariant?: string
  showBanner?: boolean
  bannerText?: string
  isHomePage?: boolean
  fullWidthBannerProps: {
    backgroundColour: string
    minHeight?: string
  }
  setAutociteV0RioOptFlag: (autociteV0RioOptFlag: boolean) => void
  setAutociteApiVersion: (autociteApiVersion: string) => void
  setUpdateMessagingFlag: (updateMessaginFlag: boolean) => void
  setParaphraseFlag: (paraPhraseFlag: boolean) => void
  setAuthModalExpFlag: (authModalExpFlag: boolean) => void
  setCitationValidityVariation: (citationValidityVariation: string) => void
  setAdUnlockExpVariation: (adUnlockVariation: AdUnlockVariations) => void
}

export const App: React.FC<Props> = ({
  children,
  userAgent,
  isCheggFree,
  head,
  navItemsSelector,
  routes,
  theme,
  exportTabIds,
  notificationBanner,
  footerContents,
  hideLayout,
  getSourceFromQuery,
  isSourceExact,
  getStyleFromQuery,
  isStyleExact,
  usesCheggLayout = false,
  useAdframeLayout = false,
  isPro,
  lvpR3,
  stickyDisabled,
  isAdsFree,
  getActualStyleId,
  adConfigVersion,
  enableSvp,
  svpVariant,
  showBanner,
  bannerText,
  isHomePage,
  fullWidthBannerProps,
  setAutociteV0RioOptFlag,
  setAutociteApiVersion,
  setUpdateMessagingFlag,
  setParaphraseFlag,
  setAuthModalExpFlag,
  setCitationValidityVariation,
  setAdUnlockExpVariation,
}) => {
  // useErrorLogger should be first to ensure we are able to log all errors
  useErrorLogger()
  const router = useRouter()
  useInitGTM()
  useAppendStylesheet()
  useAuthenticationHook()
  useDeviceID()

  useAppScroll()
  useCloseModal()
  useInitUser()
  useSetVersion()
  const { routeName } = useOnPageLoad({
    routes,
    getSourceFromQuery,
    getStyleFromQuery,
    getActualStyleId,
  })
  const { urlIsValid } = useUrlStateChange({
    getSourceFromQuery,
    isSourceExact,
    getStyleFromQuery,
    isStyleExact,
    getActualStyleId,
  })

  /**
   * Below code (optimizely reference) will be removed soon : This cod eis part of RGL-8700
   */

  // const { isEnabled: autociteV0RioEnabled, clientReady } = useSafeOptimizelyFeature(
  //   AUTOCITE_V0_RIO_EVENTS
  // )

  const autociteV0RioEnabled: boolean = false
  const clientReady: boolean = true

  useEffect(() => {
    if (clientReady) {
      setAutociteV0RioOptFlag(autociteV0RioEnabled)
    }
  }, [autociteV0RioEnabled, clientReady, setAutociteV0RioOptFlag])

  /**
   * Below code (optimizely reference) will be removed soon : This cod eis part of RGL-8700
   */

  // const {
  //   isEnabled: paraphraserEnabled,
  //   clientReady: paraphraseClientReady,
  // } = useSafeOptimizelyFeature(ENABLE_PARAPHRASER)

  const paraphraseClientReady: boolean = true
  const paraphraserEnabled: boolean = true

  useEffect(() => {
    if (paraphraseClientReady && Config.enableParaphraser) {
      setParaphraseFlag(paraphraserEnabled)
    }
  }, [paraphraserEnabled, paraphraseClientReady, setParaphraseFlag])

  // const {
  //   variation: updateAutociteApiVariation,
  //   clientReady: updateAutociteApiClientReady,
  // } = useOptimizelyExperiment(UPDATE_AUTOCITE_API)
  const updateAutociteApiVariation = 'control'
  const updateAutociteApiClientReady = true

  /**
   * Below code (optimizely reference) will be removed soon : This cod eis part of RGL-8700
   */

  // const {
  //   variation: citationValidityCmVariation,
  //   clientReady: isCitationValidityCmClientReady,
  // } = useOptimizelyExperiment(GUEST_CITATION_VALIDITY_CM)

  const citationValidityCmVariation = 'control'
  const isCitationValidityCmClientReady = true

  useEffect(() => {
    if (isCitationValidityCmClientReady && citationValidityCmVariation) {
      setCitationValidityVariation(citationValidityCmVariation)
    }
  }, [citationValidityCmVariation, isCitationValidityCmClientReady])

  // const {
  //   variation: citationValidityEbVariation,
  //   clientReady: isCitationValidityEbClientReady,
  // } = useOptimizelyExperiment(GUEST_CITATION_VALIDITY_EB)
  const citationValidityEbVariation = 'control'
  const isCitationValidityEbClientReady = true

  useEffect(() => {
    if (isCitationValidityEbClientReady && citationValidityEbVariation) {
      setCitationValidityVariation(citationValidityEbVariation)
    }
  }, [citationValidityEbVariation, isCitationValidityEbClientReady])

  useEffect(() => {
    if (updateAutociteApiClientReady) {
      setAutociteApiVersion(updateAutociteApiVariation)
    }
  }, [updateAutociteApiVariation, updateAutociteApiClientReady])

  // const {
  //   isEnabled: authModalEbExpEnabled,
  //   clientReady: authModalEbExpClientReady,
  // } = useSafeOptimizelyFeature(UPDATE_AUTH_MODAL_EB)
  const authModalEbExpEnabled = false
  const authModalEbExpClientReady = true

  useEffect(() => {
    if (authModalEbExpClientReady && Config.useAuthModalFlow) {
      setAuthModalExpFlag(authModalEbExpEnabled)
    }
  }, [authModalEbExpClientReady, authModalEbExpEnabled])

  const { appName } = Config

  // const {
  //   isEnabled: updateMessagingCMOptFlag,
  //   clientReady: updateMsgCMFeatureclientReady,
  // } = useSafeOptimizelyFeature(UPDATE_MESSAGING_FEATURE_CM)
  const updateMessagingCMOptFlag = false
  const updateMsgCMFeatureclientReady = true

  useEffect(() => {
    if (updateMsgCMFeatureclientReady && appName === 'cm') {
      setUpdateMessagingFlag(updateMessagingCMOptFlag)
    }
  }, [updateMsgCMFeatureclientReady])

  // const {
  //   variation: adUnlockModalEbVariant,
  //   clientReady: adUnlockModalEbClientReady,
  // } = useOptimizelyExperiment(AD_UNLOCK_MODAL_EB)
  const adUnlockModalEbVariant = 'control'
  const adUnlockModalEbClientReady = true

  useEffect(() => {
    if (
      adUnlockModalEbClientReady &&
      Config.adUnlockModalExperience &&
      adUnlockModalEbVariant
    ) {
      setAdUnlockExpVariation(adUnlockModalEbVariant as AdUnlockVariations)
    }
  }, [adUnlockModalEbVariant, adUnlockModalEbClientReady])

  // const {
  //   variation: adUnlockModalCmVariant,
  //   clientReady: adUnlockModalCmClientReady,
  // } = useOptimizelyExperiment(AD_UNLOCK_MODAL_CM)
  const adUnlockModalCmVariant = 'control'
  const adUnlockModalCmClientReady = true

  useEffect(() => {
    if (
      adUnlockModalCmClientReady &&
      Config.adUnlockModalExperience &&
      adUnlockModalCmVariant
    ) {
      setAdUnlockExpVariation(adUnlockModalCmVariant as AdUnlockVariations)
    }
  }, [adUnlockModalCmVariant, adUnlockModalCmClientReady])

  const NotificationBanner = notificationBanner
  const hideAdRail = authRoutes.includes(router.pathname)
  const errorPageMaybe = urlIsValid ? null : <Error404 />

  // determine whether we should should the lvp for a particular page and given the user role
  const showLvp =
    !!hasSideViewForRoute(routeName) && !isAdsFree && !isPro && !useAdframeLayout
  const showSvp = enableSvp && useAdframeLayout && !isAdsFree && !isPro
  const showLvpR3 = !!lvpR3 && !!hasSideViewForRoute(routeName) && !isAdsFree && !isPro
  const isStickyDisabled =
    !!stickyDisabled && !!hasSideViewForRoute(routeName) && !isAdsFree && !isPro

  const props = {
    isCheggFree,
    exportTabIds,
    children,
    hideLayout,
    hideAdRail,
    useAdframeLayout,
    errorPageMaybe,
    navItemsSelector,
    NotificationBanner,
    footerContents,
    routeName,
    isAdsFree,
    isPro,
    showLvp,
    showSvp,
    svpVariant,
    showBanner,
    adConfigVersion,
    bannerText,
    isHomePage,
    fullWidthBannerProps,
  }

  return (
    <>
      <AppHeadTags {...head} />
      <CheggLayoutProvider userAgent={userAgent} skip={!usesCheggLayout}>
        {/* Using any as @chegg-ui/theme requires it to pass cuiTheme property in theme but we are using different custom theme in UCF */}
        <ThemeProvider theme={theme as any}>
          <RoutesContext.Provider value={routes}>
            <LvpProvider
              adConfigVersion={adConfigVersion}
              showLvp={showLvp}
              isLvpR3Active={showLvpR3}
              isStickyDisabled={isStickyDisabled}
              currentPage={useCurrentPage()}
            >
              <AppLayout {...props} />
            </LvpProvider>
          </RoutesContext.Provider>
        </ThemeProvider>
      </CheggLayoutProvider>
    </>
  )
}

const mapStateToProps = (state: AppState) => ({
  isPro: isProSelector(state),
  lvpR3: lvpR3LayoutSelector(state),
  stickyDisabled: stickyDisabledSelector(state),
  adConfigVersion: adConfigVersionSelector(state),
  svpVariant: svpVariantSelector(state),
})

const mapDispatchToProps = {
  setAutociteV0RioOptFlag,
  setUpdateMessagingFlag,
  setAutociteApiVersion,
  setParaphraseFlag,
  setAuthModalExpFlag,
  setCitationValidityVariation,
  setAdUnlockExpVariation,
}

// export default connect(mapStateToProps, mapDispatchToProps)(withOptimizelyComponent(App))
export default connect(mapStateToProps, mapDispatchToProps)(App)
